import React, { createContext, useEffect, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  LOGIN_URL,
  GET_USER_DETAILS_URL,
  registerOtpValidateUrl,
} from "../Constant/api";
import { LoadingScreen } from "../Components";
import { authService, unAuthService } from "../Service/Axios";
import { AUTH_TOKEN, REFRESH_TOKEN, USER_TYPE } from "../Constant/constants";
import Cookie from "../Service/Cookie";
import { paths } from "../Constant/paths";

const initialAuthState = {
  isAuthenticated: false,
  isOnboardingCompleted: false,
  isInitialised: false,
  user: null,
  messageList: [],
  isSiderbarToggle: false,
  resetGlobalSearch: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INITIALISE": {
      const { isAuthenticated, user, isOnboardingCompleted } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isOnboardingCompleted,
        isInitialised: true,
        user,
      };
    }
    case "LOGIN": {
      return {
        ...state,
        isAuthenticated: true,
        isOnboardingCompleted: action.payload?.onboarding,
        user: action.payload,
      };
    }
    case "USER_UPDATE": {
      return {
        ...state,
        user: action.payload,
        isOnboardingCompleted: action.payload?.onboarding,
      };
    }
    case "REGISTER": {
      return {
        ...state,
        isAuthenticated: true,
        isOnboardingCompleted: action.payload?.onboarding,
        user: action.payload,
      };
    }

    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        isOnboardingCompleted: false,
        user: null,
      };
    }

    case "UPDATE_MESSAGE": {
      let index = state.messageList.findIndex((obj) => obj.text === "Loading");
      if (index > -1) {
        state.messageList.splice(index, 1);
      }
      return {
        ...state,
        messageList: [...state.messageList, { ...action.payload }],
      };
    }

    case "SIDEBAR_TOGGLE": {
      return {
        ...state,
        isSiderbarToggle: !state.isSiderbarToggle,
      };
    }

    case "CONTACT_SALES_TOGGLE": {
      return {
        ...state,
        contactSalesModal: !state.contactSalesModal,
      };
    }
    case "RESET_GLOBAL_SEARCH": {
      return {
        ...state,
        resetGlobalSearch: true,
      };
    }
    case "CLEAR_GLOBAL_SEARCH": {
      return {
        ...state,
        resetGlobalSearch: false,
      };
    }

    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  Login: () => Promise.resolve(),
  RegisterWithOtpValidate: () => Promise.resolve(),
  Logout: () => {},
  updateUser: () => {},
  updateMessage: () => Promise.resolve(),
  StoreData: () => Promise.resolve(),
  toggleSidebar: () => Promise.resolve(),
  toggleContactSalesModal: () => Promise.resolve(),
  triggerGlobalSearchReset: () => Promise.resolve(),
  clearGlobalSearchReset: () => Promise.resolve(),
});

export function AuthProvider({ children }) {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const Login = async (payload) => {
    try {
      const response = await unAuthService(LOGIN_URL, "POST", payload);
      if (response?.success) {
        Cookie.setCookie(AUTH_TOKEN, response.data, 1);
        Cookie.setCookie(REFRESH_TOKEN, response.refreshToken, 30);
        const userData = {
          ...response,
          data: undefined,
          message: undefined,
          refreshToken: undefined,
          success: undefined,
        };
        dispatch({ type: "LOGIN", payload: userData });
        toast.success(
          t("common.messages.notification.success.validCredentials")
        );
        const isFormAplication = localStorage.getItem("isFormAplication");
        const redirectPath = localStorage.getItem("redirectAfterLogin");
        if (isFormAplication) {
          navigate(isFormAplication);
          localStorage.removeItem("isFormAplication");
        } else if (redirectPath) {
          navigate(redirectPath);
          localStorage.removeItem("redirectAfterLogin");
        } else {
          if (response.type === USER_TYPE.Applicant) {
            navigate(paths.applicant.dashboard);
          } else {
            navigate(paths.admin.dashboard);
          }
        }
        return response;
      } else {
        return response;
      }
    } catch (error) {
      return error?.response?.data;
    }
  };

  const RegisterWithOtpValidate = async (payload) => {
    const response = await unAuthService(
      registerOtpValidateUrl,
      "POST",
      payload
    );
    if (response && response.success) {
      if (response.message === "The otp is valid") {
        Cookie.setCookie(AUTH_TOKEN, response.data, 1);
        Cookie.setCookie(REFRESH_TOKEN, response.refreshToken, 30);
        const userData = {
          ...response,
          data: undefined,
          message: undefined,
          refreshToken: undefined,
          success: undefined,
        };
        dispatch({ type: "REGISTER", payload: userData });
        toast.success(
          t("common.messages.notification.success.validRegistrations")
        );
        navigate(paths.common.onboarding);
        return response;
      } else {
        return response;
      }
    } else {
      toast.error(
        response?.message ||
          t("common.messages.notification.failed.somethingWentWrong")
      );
    }
  };

  const Logout = () => {
    dispatch({ type: "LOGOUT" });
    Cookie.deleteCookie(AUTH_TOKEN);
    navigate("/");
  };

  const updateUser = (user) => {
    dispatch({ type: "USER_UPDATE", payload: user });
  };

  const updateMessage = (message) => {
    dispatch({ type: "UPDATE_MESSAGE", payload: message });
  };

  const toggleSidebar = () => {
    dispatch({ type: "SIDEBAR_TOGGLE" });
  };

  const toggleContactSalesModal = () => {
    dispatch({ type: "CONTACT_SALES_TOGGLE" });
  };

  const triggerGlobalSearchReset = () => {
    dispatch({ type: "RESET_GLOBAL_SEARCH" });
  };

  const clearGlobalSearchReset = () => {
    dispatch({ type: "CLEAR_GLOBAL_SEARCH" });
  };

  useEffect(() => {
    const linkElement = document.createElement("link");
    linkElement.rel = "stylesheet";
    linkElement.type = "text/css";
    if (state.user && state.user.colorPlateType) {
      linkElement.href = `/assets/css/color-theme-${state.user.colorPlateType}.css`;
    } else {
      linkElement.href = "/assets/css/color-theme-1.css";
    }
    document.head.appendChild(linkElement);

    return () => {
      document.head.removeChild(linkElement);
    };
  }, [state.user]);

  useEffect(() => {
    const initialise = async () => {
      try {
        const data = {};
        const Token = Cookie.getCookie(AUTH_TOKEN);
        if (Token) {
          const response = await authService(
            GET_USER_DETAILS_URL,
            "POST",
            data
          );
          if (response?.success) {
            const userData = {
              ...response,
              data: undefined,
              message: undefined,
              refreshToken: undefined,
              success: undefined,
            };
            dispatch({
              type: "INITIALISE",
              payload: {
                isAuthenticated: true,
                isOnboardingCompleted: userData?.onboarding,
                user: userData,
              },
            });
          } else {
            toast.error(response.message);
          }
        } else {
          dispatch({
            type: "INITIALISE",
            payload: {
              isAuthenticated: false,
              user: null,
              isInitialised: true,
            },
          });
        }
      } catch (err) {
        // toast.error("Error - Please Try Again");
        dispatch({
          type: "INITIALISE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        Login,
        RegisterWithOtpValidate,
        updateUser,
        Logout,
        updateMessage,
        toggleSidebar,
        toggleContactSalesModal,
        triggerGlobalSearchReset,
        clearGlobalSearchReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
